import React from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '../hooks/usePageTitle';

const MobileApps: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('Mobile Apps');

  return (
    <div className="min-h-screen">
      {/* Header Section */}
      <div
        className="relative py-20"
        style={{
          backgroundImage: `url('/hero-background.webp')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="container mx-auto px-8">
          <button
            onClick={() => navigate('/')}
            className="mb-6 text-white hover:text-orange-400 flex items-center transition"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Terug naar Home
          </button>
          <h1 className="text-5xl font-bold text-white mb-4">Mobile Apps</h1>
          <p className="text-xl text-white/90 max-w-3xl">
            Native en cross-platform apps voor iOS en Android
          </p>
        </div>
      </div>

      {/* Content Section */}
      <div className="container mx-auto px-8 py-16">
        <div className="max-w-4xl mx-auto">
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-blue-900 mb-6">Bereik je klanten overal</h2>
            <p className="text-lg text-gray-700 mb-4">
              In een wereld waar meer dan 70% van het internetverkeer via mobiele apparaten loopt,
              is een mobiele app vaak niet meer weg te denken. Bij SnapLogic ontwikkelen we
              krachtige, gebruiksvriendelijke apps die jouw bedrijf naar een hoger niveau tillen.
            </p>
          </section>

          {/* Platform Options */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-blue-900 mb-6">Onze oplossingen</h2>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border-2 border-blue-200">
                <div className="text-4xl mb-3">📱</div>
                <h3 className="text-xl font-bold text-blue-800 mb-3">iOS Apps</h3>
                <p className="text-gray-700 mb-4">
                  Native iOS apps gebouwd met Swift/SwiftUI voor optimale performance en
                  gebruikerservaring op iPhone en iPad.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>✓ App Store publicatie</li>
                  <li>✓ Apple Design Guidelines</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-green-50 to-green-100 p-6 rounded-xl border-2 border-green-200">
                <div className="text-4xl mb-3">🤖</div>
                <h3 className="text-xl font-bold text-green-800 mb-3">Android Apps</h3>
                <p className="text-gray-700 mb-4">
                  Robuuste Android apps met Kotlin voor het volledige Android ecosysteem,
                  van smartphones tot tablets.
                </p>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>✓ Google Play Store publicatie</li>
                  <li>✓ Breed apparaat support</li>
                </ul>
              </div>

              <div className="bg-gradient-to-br from-purple-50 to-purple-100 p-6 rounded-xl border-2 border-purple-200 md:col-span-2">
                <div className="text-4xl mb-3">🌐</div>
                <h3 className="text-xl font-bold text-purple-800 mb-3">Cross-Platform Apps</h3>
                <p className="text-gray-700 mb-4">
                  Eén codebase voor iOS én Android met React Native. Snellere ontwikkeling,
                  lagere kosten, zonder in te leveren op kwaliteit.
                </p>
                <ul className="grid md:grid-cols-2 gap-4 text-sm text-gray-700">
                  <li>✓ Snellere time-to-market</li>
                  <li>✓ Consistente UX op beide platforms</li>
                </ul>
              </div>
            </div>
          </section>

          {/* Process */}
          <section className="mb-12">
            <h2 className="text-3xl font-bold text-blue-900 mb-6">Van idee naar App Store</h2>
            <div className="space-y-6">
              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                  1
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Concept & UX Design</h3>
                  <p className="text-gray-700">
                    We ontwerpen de user flow en interface met focus op gebruiksgemak en conversie.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                  2
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Backend Development</h3>
                  <p className="text-gray-700">
                    Schaalbare API's en databases die de app van data voorzien.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                  3
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">App Development</h3>
                  <p className="text-gray-700">
                    Native of cross-platform development met regelmatige updates en testversies.
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <div className="bg-orange-500 text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 flex-shrink-0 font-bold">
                  4
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-800 mb-2">Testing & Launch</h3>
                  <p className="text-gray-700">
                    Uitgebreid testen, beta programma en publicatie in de App Stores.
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* CTA */}
          <section className="bg-blue-900 text-white p-8 rounded-xl text-center">
            <h2 className="text-3xl font-bold mb-4">Jouw app-idee verwezenlijken?</h2>
            <p className="text-xl mb-6">
              Laat ons je helpen met een krachtige mobiele app
            </p>
            <button
              onClick={() => navigate('/?step=1')}
              className="px-8 py-3 rounded-lg font-bold transition transform hover:scale-105"
              style={{
                background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
              }}
            >
              Start je app project
            </button>
          </section>
        </div>
      </div>
    </div>
  );
};

export default MobileApps;
